import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useAudio } from '../context/AudioContext'
import { useDevMode } from '../context/DevModeContext'
import { useAuth } from '../context/AuthContext'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../config/firebase'
import { Capacitor, registerPlugin } from '@capacitor/core'
import { shareAudioWithText, shareNoteFor, downloadBlob, audioFileName } from '../lib/shareAudio'
import { VoiceRecorder } from 'capacitor-voice-recorder'
import { Filesystem, Directory } from '@capacitor/filesystem'

const AudioFix = registerPlugin('AudioFix')
import breakpointsData from '../data/audio-breakpoints.json'

const isNativePlatform = Capacitor.isNativePlatform()

// Handing a recording straight to the share sheet works on phones. On desktop
// it does not: the Windows dialog opens and lists Telegram and WhatsApp, but
// the attachment is dropped and only the text arrives. Desktop therefore takes
// the two-step route — save the file, then attach it — which is slower but
// actually delivers the audio.
function prefersDirectShare() {
  if (navigator.userAgentData) return navigator.userAgentData.mobile === true
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '')
}

// Link opened inside a social app's embedded webview. These commonly expose no
// microphone at all, so getUserMedia is missing rather than merely blocked.
function inAppBrowser() {
  return /FBAN|FBAV|Instagram|Line\/|Telegram|WhatsApp|WebView|; wv\)/i.test(navigator.userAgent || '')
}

// A dead Record button is the worst outcome, so say exactly what happened and
// what to do about it. Browsers reject without prompting when permission was
// blocked earlier, so "no prompt appeared" is itself a symptom.
function micErrorMessage(err) {
  switch (err?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone blocked. Click the padlock in the address bar → Site settings → Microphone → Allow, then reload. On a phone, check site permissions in your browser settings.'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone found. Plug one in, or check your system sound settings.'
    case 'NotReadableError':
    case 'TrackStartError':
      return 'The microphone is in use by another app. Close it (Zoom, Teams, Meet) and try again.'
    case 'OverconstrainedError':
      return 'This microphone does not support the requested settings.'
    case 'AbortError':
      return 'The microphone stopped unexpectedly. Try again.'
    default:
      return 'Could not start recording: ' + (err?.message || err)
  }
}

// Extract filename from a Firebase Storage URL
function getFilenameFromUrl(url) {
  if (!url) return null
  try {
    const match = url.match(/audios%2F([^?]+)/)
    if (match) return decodeURIComponent(match[1])
    const parts = url.split('/').pop()?.split('?')[0]
    return parts ? decodeURIComponent(parts) : null
  } catch {
    return null
  }
}

// A token is a real word if it contains at least one Ethiopic letter (U+1200-U+137F)
const isGeezWord = (token) => /[\u1200-\u137F]/.test(token)

// Practice mode steps
const STEP_LISTEN = 'listen'
const STEP_RECORD = 'record'
const STEP_COMPARE = 'compare'

export default function AudioPlayer({ audioUrl, sectionId, categoryId, lessonLabel, geezText, translationText, onTimeSync, onMarkingWords, onRecordingChange, seekRequest, savedWordTimestamps, onProgress }) {
  const audioRef = useRef(null)
  const { playingAudioId, setPlayingAudioId } = useAudio()
  const { devMode } = useDevMode()
  const { user } = useAuth()
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioId = `audio-${sectionId}`
  const wasLastActiveRef = useRef(false) // track if this audio was last played

  // Look up breakpoints for this audio file
  const breakpoints = useMemo(() => {
    const filename = getFilenameFromUrl(audioUrl)
    return filename ? (breakpointsData[filename] || []) : []
  }, [audioUrl])

  // Recording state
  const mediaRecorderRef = useRef(null)
  const chunksRef = useRef([])
  const recordingTimerRef = useRef(null)
  const recordedAudioRef = useRef(null)
  const recordingBlobRef = useRef(null)
  const shareUrlRef = useRef(null) // object URL for saving/sharing
  const [isRecording, setIsRecording] = useState(false)
  const [recordedBlobUrl, setRecordedBlobUrl] = useState(null)
  const [recordingTime, setRecordingTime] = useState(0)
  const [isPlayingRecording, setIsPlayingRecording] = useState(false)
  const [recProgress, setRecProgress] = useState(0)
  const [recDuration, setRecDuration] = useState(0)
  const [recordingError, setRecordingError] = useState(null)
  const [shareNote, setShareNote] = useState('')   // what the last share attempt did
  const [savedFileName, setSavedFileName] = useState('') // set when a recording is saved to attach by hand
  const [debugInfo, setDebugInfo] = useState('') // temporary debug

  // Practice mode state
  const [practiceMode, setPracticeMode] = useState(false)
  const [practiceStep, setPracticeStep] = useState(STEP_LISTEN)
  const [pieceIndex, setPieceIndex] = useState(0)
  const [isComparing, setIsComparing] = useState(false)
  const practiceStopRef = useRef(null) // time to auto-stop at

  // Breakpoint times in seconds (including 0 and end)
  const breakpointTimes = useMemo(() => {
    if (!duration) return [0]
    if (breakpoints.length === 0) return [0, duration]
    return [0, ...breakpoints.map(bp => (bp / 100) * duration), duration]
  }, [breakpoints, duration])

  const totalPieces = breakpointTimes.length > 1 ? breakpointTimes.length - 1 : 1

  // Dev-mode word marking state
  const [markingMode, setMarkingMode] = useState(false)
  const [wordMarks, setWordMarks] = useState([])
  const wordMarksRef = useRef([]) // always-current ref to avoid stale closures
  const wordsRef = useRef([]) // the words from geezText

  // Auto-review (segment-based Yes/No/Undo) state
  const [reviewActive, setReviewActive] = useState(false)
  const [reviewStatus, setReviewStatus] = useState('')
  const [reviewDips, setReviewDips] = useState([])
  const [reviewSegIndex, setReviewSegIndex] = useState(0)
  const [reviewWordIndex, setReviewWordIndex] = useState(0)
  const [reviewExtendCount, setReviewExtendCount] = useState(0)
  const [reviewTimestamps, setReviewTimestamps] = useState([])
  const [reviewGrouped, setReviewGrouped] = useState(new Set())
  const reviewHistoryRef = useRef([])
  const reviewDurationRef = useRef(0)
  const reviewDipsRef = useRef([])
  const reviewSegIndexRef = useRef(0)
  const reviewWordIndexRef = useRef(0)
  const reviewExtendCountRef = useRef(0)
  const reviewTimestampsRef = useRef([])
  const reviewGroupedRef = useRef(new Set())
  const segmentStopRef = useRef(null) // timeupdate listener cleanup

  // Get start/end times for a given piece index
  function getPieceTimes(idx) {
    const start = breakpointTimes[idx] || 0
    const end = breakpointTimes[idx + 1] || duration || 0
    return { start, end }
  }

  // React to word-click seek requests from parent
  useEffect(() => {
    if (!seekRequest || !audioRef.current) return
    if (seekRequest.time !== undefined) {
      audioRef.current.currentTime = seekRequest.time
    } else if (seekRequest.wordIndex !== undefined && seekRequest.totalWords > 0) {
      const dur = audioRef.current.duration || 0
      audioRef.current.currentTime = (seekRequest.wordIndex / seekRequest.totalWords) * dur
    }
    audioRef.current.play()
    setPlayingAudioId(audioId)
    setIsPlaying(true)
  }, [seekRequest])

  // --- High-frequency time sync via requestAnimationFrame ---
  const rafRef = useRef(null)

  useEffect(() => {
    function tick() {
      if (audioRef.current && !audioRef.current.paused) {
        if (onTimeSync) onTimeSync(audioRef.current.currentTime)
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    if (isPlaying) {
      rafRef.current = requestAnimationFrame(tick)
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [isPlaying, onTimeSync])

  // --- Core audio controls ---

  useEffect(() => {
    if (playingAudioId === audioId) {
      wasLastActiveRef.current = true
    } else if (playingAudioId) {
      // Another audio started — this one is no longer last active
      wasLastActiveRef.current = false
      if (isPlaying) {
        if (audioRef.current) audioRef.current.pause()
        setIsPlaying(false)
      }
    }
  }, [playingAudioId, audioId, isPlaying])

  useEffect(() => {
    setIsPlaying(false)
    setProgress(0)
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    stopRecording()
    deleteRecording()
    exitPractice()
  }, [audioUrl])

  useEffect(() => {
    if (onRecordingChange) onRecordingChange(recordedBlobUrl)
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current)
    }
  }, [recordedBlobUrl])

  function togglePlay() {
    if (!audioRef.current) return
    if (isRecording) stopRecording()
    if (isPlayingRecording) stopRecordingPlayback()

    if (isPlaying) {
      audioRef.current.pause()
      setPlayingAudioId(null)
      setIsPlaying(false)
    } else {
      // In practice mode, clear the stop ref so normal play doesn't auto-stop
      if (!practiceMode) practiceStopRef.current = null
      audioRef.current.play()
      setPlayingAudioId(audioId)
      setIsPlaying(true)
    }
  }

  function handleTimeUpdate() {
    if (!audioRef.current) return
    const current = audioRef.current.currentTime
    const total = audioRef.current.duration
    setProgress(total ? (current / total) * 100 : 0)

    // Send current time to parent for word highlighting
    if (onTimeSync) onTimeSync(current)

    // Report listening progress
    if (onProgress && total > 0) onProgress(current, total)

    // Auto-stop at breakpoint in practice mode
    if (practiceMode && practiceStopRef.current !== null && current >= practiceStopRef.current - 0.05) {
      audioRef.current.pause()
      audioRef.current.currentTime = practiceStopRef.current
      setIsPlaying(false)
      setPlayingAudioId(null)
      practiceStopRef.current = null

      // Auto-advance to next step
      if (practiceStep === STEP_LISTEN) {
        setPracticeStep(STEP_RECORD)
      } else if (practiceStep === STEP_COMPARE && isComparing) {
        // Lesson piece finished during compare, now play recording
        setIsComparing(false)
        playRecordingForCompare()
      }
    }
  }

  function handleLoadedMetadata() {
    if (audioRef.current) setDuration(audioRef.current.duration)
  }

  function handleSeek(e) {
    if (!audioRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = x / rect.width
    audioRef.current.currentTime = percent * audioRef.current.duration
  }

  function handleEnded() {
    setIsPlaying(false)
    setProgress(0)
    setPlayingAudioId(null)
    if (onTimeSync) onTimeSync(-1)
    // Mark full listen on end
    if (onProgress && audioRef.current?.duration) onProgress(audioRef.current.duration, audioRef.current.duration)

    if (practiceMode && practiceStep === STEP_LISTEN) {
      setPracticeStep(STEP_RECORD)
    }
  }

  function formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // --- Breakpoint navigation ---

  function jumpToNextBreakpoint() {
    if (!audioRef.current || !duration) return
    const currentTime = audioRef.current.currentTime
    const next = breakpointTimes.find(t => t > currentTime + 0.5)
    if (next !== undefined && next < duration) {
      audioRef.current.currentTime = next
    }
  }

  function jumpToPrevBreakpoint() {
    if (!audioRef.current || !duration) return
    const currentTime = audioRef.current.currentTime
    const prev = [...breakpointTimes].reverse().find(t => t < currentTime - 1)
    audioRef.current.currentTime = prev !== undefined ? prev : 0
  }

  // Keyboard controls
  useEffect(() => {
    function handleKeyDown(e) {
      if (playingAudioId !== audioId && !practiceMode && !wasLastActiveRef.current) return
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault()
          if (practiceMode) {
            practiceNext()
          } else {
            jumpToNextBreakpoint()
          }
          break
        case 'ArrowLeft':
          e.preventDefault()
          if (practiceMode) {
            practicePrev()
          } else {
            jumpToPrevBreakpoint()
          }
          break
        case ' ':
          e.preventDefault()
          if (practiceMode && practiceStep === STEP_RECORD && !isRecording) {
            practiceRecordPiece()
          } else if (practiceMode && practiceStep === STEP_RECORD && isRecording) {
            stopRecording()
          } else {
            togglePlay()
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [playingAudioId, audioId, breakpointTimes, duration, isPlaying, practiceMode, practiceStep, isRecording])

  // --- Recording functions ---

  // Live audio level monitoring ref
  const analyserRef = useRef(null)
  const levelRafRef = useRef(null)
  const streamRef = useRef(null)
  const micTapRef = useRef(null)   // zero-gain node keeping the graph alive on Safari
  const [micLevel, setMicLevel] = useState(0)
  // 'none' = no usable track, 'silent' = track live but the analyser never saw
  // a non-zero sample, 'live' = real signal has arrived at least once.
  const [micStatus, setMicStatus] = useState('none')

  async function startRecording() {
    setRecordingError(null)
    setShareNote('')
    setSavedFileName('')
    setDebugInfo('')
    try {
      // Recording needs a secure origin. Browsers hide mediaDevices entirely
      // over plain http, so the request never happens and no prompt appears.
      if (!window.isSecureContext) {
        setRecordingError('Recording needs a secure (https) connection. Open https://kidase.geezword.com and try again.')
        return false
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // Usually an in-app browser: Telegram, WhatsApp, Facebook and Instagram
        // open links in a webview that blocks the microphone outright.
        setRecordingError(inAppBrowser()
          ? 'This in-app browser cannot record. Tap the ⋯ menu and choose "Open in browser", then try again.'
          : 'This browser cannot record audio. Try Chrome, Edge or Safari.')
        return false
      }

      // Request mic with explicit constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: true,
        }
      })
      streamRef.current = stream

      // Check if we actually got an audio track
      const audioTracks = stream.getAudioTracks()
      setDebugInfo(`Got ${audioTracks.length} audio track(s): ${audioTracks.map(t => t.label + '/' + t.readyState + (t.muted ? '/muted' : '')).join(', ')}`)

      const micTrack = audioTracks[0]
      if (!micTrack || micTrack.readyState !== 'live') {
        setRecordingError('No microphone track — check microphone permission for this site')
        setMicStatus('none')
        stream.getTracks().forEach(t => t.stop())
        return false
      }
      // A track can be live but muted at the OS level (another app holding the
      // mic, or hardware mute). Report that instead of showing a dead meter.
      setMicStatus(micTrack.muted ? 'none' : 'silent')
      micTrack.onmute = () => setMicStatus('none')
      micTrack.onunmute = () => setMicStatus('silent')

      if (isPlaying && audioRef.current) {
        audioRef.current.pause()
        setPlayingAudioId(null)
        setIsPlaying(false)
      }
      if (isPlayingRecording) stopRecordingPlayback()
      deleteRecording()

      // Set up live audio level meter using AnalyserNode
      const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)()
      audioCtxRef.current = ctx
      if (ctx.state === 'suspended') await ctx.resume()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      // Safari (iOS especially) only pumps a MediaStreamSource when the graph
      // reaches a destination; without this the analyser reads flat silence
      // even though MediaRecorder is capturing fine.
      const silentTap = ctx.createGain()
      silentTap.gain.value = 0
      analyser.connect(silentTap)
      silentTap.connect(ctx.destination)
      micTapRef.current = silentTap
      analyserRef.current = analyser

      // Monitor audio level in real-time
      // Time-domain data is fftSize samples, not frequencyBinCount (half that).
      const dataArray = new Uint8Array(analyser.fftSize)
      function monitorLevel() {
        if (!analyserRef.current) return
        analyserRef.current.getByteTimeDomainData(dataArray)
        let max = 0
        for (let i = 0; i < dataArray.length; i++) {
          const v = Math.abs(dataArray[i] - 128)
          if (v > max) max = v
        }
        // Any non-zero deviation means audio is genuinely flowing; quiet speech
        // is not the same thing as a dead microphone.
        if (max > 0) setMicStatus(prev => (prev === 'silent' ? 'live' : prev))
        setMicLevel(max / 128) // 0.0 to 1.0
        levelRafRef.current = requestAnimationFrame(monitorLevel)
      }
      levelRafRef.current = requestAnimationFrame(monitorLevel)

      // Set up MediaRecorder
      chunksRef.current = []
      // Prefer AAC-in-mp4 (WhatsApp/Windows compatible), fall back to webm
      const mimeType =
        MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2') ? 'audio/mp4;codecs=mp4a.40.2'
        : MediaRecorder.isTypeSupported('audio/mp4;codecs=aac') ? 'audio/mp4;codecs=aac'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : ''
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        // Stop level monitoring
        if (levelRafRef.current) {
          cancelAnimationFrame(levelRafRef.current)
          levelRafRef.current = null
        }
        analyserRef.current = null
        if (micTapRef.current) {
          try { micTapRef.current.disconnect() } catch {}
          micTapRef.current = null
        }
        setMicLevel(0)
        setMicStatus('none')

        const blob = new Blob(chunksRef.current, { type: recorder.mimeType })
        recordingBlobRef.current = blob
        stream.getTracks().forEach(t => t.stop())
        streamRef.current = null

        // Reset audio mode on Android
        if (isNativePlatform) {
          try { AudioFix.resetAudioMode() } catch {}
        }

        setDebugInfo(`Recorded: mime=${recorder.mimeType}, size=${blob.size}`)

        // Create object URL for saving/sharing
        shareUrlRef.current = URL.createObjectURL(blob)

        const reader = new FileReader()
        reader.onloadend = () => setRecordedBlobUrl(reader.result)
        reader.readAsDataURL(blob)

        if (practiceMode) {
          setPracticeStep(STEP_COMPARE)
        }
      }

      recorder.start(250) // collect data every 250ms
      setIsRecording(true)
      setRecordingTime(0)
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1)
      }, 1000)
      return true
    } catch (err) {
      console.error('Recording error:', err)
      setRecordingError(micErrorMessage(err))
      return false
    }
  }

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
    if (levelRafRef.current) {
      cancelAnimationFrame(levelRafRef.current)
      levelRafRef.current = null
    }
    analyserRef.current = null
    if (micTapRef.current) {
      try { micTapRef.current.disconnect() } catch {}
      micTapRef.current = null
    }
    setMicLevel(0)
    setIsRecording(false)
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
      recordingTimerRef.current = null
    }
  }, [])

  function toggleRecord() {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  // Store original lesson URL so we can restore it after playing recording
  const lessonUrlRef = useRef(audioUrl)
  useEffect(() => { lessonUrlRef.current = audioUrl }, [audioUrl])

  // Web Audio API refs for playback
  const audioCtxRef = useRef(null)
  const sourceNodeRef = useRef(null)
  const audioBufferRef = useRef(null)
  const webAudioStartRef = useRef(0)
  const webAudioRafRef = useRef(null)

  function togglePlayRecording() {
    if (!recordingBlobRef.current) return

    if (isPlayingRecording) {
      // Stop Web Audio playback
      if (sourceNodeRef.current) {
        try { sourceNodeRef.current.stop() } catch {}
        sourceNodeRef.current = null
      }
      if (webAudioRafRef.current) {
        cancelAnimationFrame(webAudioRafRef.current)
        webAudioRafRef.current = null
      }
      // The <audio> fallback may be the one actually playing
      if (recordedAudioRef.current) recordedAudioRef.current.pause()
      setIsPlayingRecording(false)
    } else {
      // Stop lesson audio if playing
      if (isPlaying && audioRef.current) {
        audioRef.current.pause()
        setPlayingAudioId(null)
        setIsPlaying(false)
      }

      // Decode and play via Web Audio API (bypasses <audio> element entirely)
      const blob = recordingBlobRef.current
      blob.arrayBuffer().then(arrayBuf => {
        const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)()
        audioCtxRef.current = ctx
        if (ctx.state === 'suspended') ctx.resume()
        return ctx.decodeAudioData(arrayBuf)
      }).then(decodedBuffer => {
        audioBufferRef.current = decodedBuffer
        setRecDuration(decodedBuffer.duration)

        // Check if audio data is silence or has real content
        const samples = decodedBuffer.getChannelData(0)
        let maxAmp = 0
        let rms = 0
        for (let i = 0; i < samples.length; i++) {
          const abs = Math.abs(samples[i])
          if (abs > maxAmp) maxAmp = abs
          rms += samples[i] * samples[i]
        }
        rms = Math.sqrt(rms / samples.length)
        setDebugInfo(`Decoded: ${decodedBuffer.duration.toFixed(1)}s, ${decodedBuffer.sampleRate}Hz, maxAmp=${maxAmp.toFixed(4)}, rms=${rms.toFixed(6)}`)

        const ctx = audioCtxRef.current
        const source = ctx.createBufferSource()
        source.buffer = decodedBuffer
        source.connect(ctx.destination)
        source.start(0)
        sourceNodeRef.current = source
        webAudioStartRef.current = ctx.currentTime
        setIsPlayingRecording(true)

        source.onended = () => {
          sourceNodeRef.current = null
          setIsPlayingRecording(false)
          setRecProgress(0)
          if (webAudioRafRef.current) {
            cancelAnimationFrame(webAudioRafRef.current)
            webAudioRafRef.current = null
          }
        }

        // Progress tracking
        function tick() {
          if (!sourceNodeRef.current || !audioCtxRef.current) return
          const elapsed = audioCtxRef.current.currentTime - webAudioStartRef.current
          const total = decodedBuffer.duration
          setRecProgress(total ? (elapsed / total) * 100 : 0)
          webAudioRafRef.current = requestAnimationFrame(tick)
        }
        webAudioRafRef.current = requestAnimationFrame(tick)
      }).catch(err => {
        // Some browsers record a container decodeAudioData won't accept (Chrome
        // on Windows records audio/mp4 and then rejects it). The same blob is
        // already mounted on the <audio> element that practice mode's compare
        // step plays, so fall back to that rather than failing the button.
        setDebugInfo(`WebAudio decode failed (${err.message}); using <audio> fallback`)
        const el = recordedAudioRef.current
        if (!el) return
        el.currentTime = 0
        el.play().then(() => setIsPlayingRecording(true)).catch(playErr => {
          setDebugInfo(`Recording will not play: ${playErr.message}`)
        })
      })
    }
  }

  function handleRecSeek(e) {
    if (!recordedAudioRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = x / rect.width
    recordedAudioRef.current.currentTime = percent * recordedAudioRef.current.duration
  }

  const deleteRecording = useCallback(() => {
    setRecordedBlobUrl(null)
    recordingBlobRef.current = null
    if (shareUrlRef.current) {
      URL.revokeObjectURL(shareUrlRef.current)
      shareUrlRef.current = null
    }
    setIsPlayingRecording(false)
    setRecProgress(0)
    setRecDuration(0)
  }, [])

  // Get file extension based on recording MIME type
  function getRecordingExt() {
    const blob = recordingBlobRef.current
    if (!blob) return 'm4a'
    const type = blob.type || ''
    if (type.includes('mp4')) return 'm4a'
    if (type.includes('mpeg')) return 'mp3'
    return 'webm'
  }

  // Share recording — native share on mobile, download fallback on desktop
  async function shareRecording() {
    const blob = recordingBlobRef.current
    if (!blob) return
    const name = audioFileName(lessonLabel || `Kidase ${sectionId}`, blob.type)

    // Desktop: save it and say so plainly. Never claim it was sent.
    if (!prefersDirectShare()) {
      downloadBlob(blob, name)
      setSavedFileName(name)
      setShareNote('')
      setDebugInfo('saved for manual attach: ' + name + ' (' + blob.type + ', ' + blob.size + 'B)')
      return
    }

    // Phones: the share sheet does deliver the audio, so keep that behaviour.
    const result = await shareAudioWithText({
      blob,
      fileName: name,
      title: lessonLabel || 'Kidase Lesson',
      text: [
        lessonLabel ? lessonLabel + '\n\n' : '',
        geezText,
        translationText ? '\n' + translationText : '',
        '\n\n-- Sirate Kidase Tutor'
      ].filter(Boolean).join(''),
    })
    setSavedFileName('')
    setShareNote(shareNoteFor(result))
    setDebugInfo('share outcome=' + result.outcome + ' type=' + blob.type + ' size=' + blob.size)
  }

  // Save recording to device storage
  function saveRecording() {
    const blob = recordingBlobRef.current
    if (!blob) return
    const date = new Date().toISOString().slice(0, 10)
    downloadBlob(blob, audioFileName(`${lessonLabel || sectionId} ${date}`, blob.type))
  }

  // --- Practice mode functions ---

  function enterPractice() {
    // Stop everything first
    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setPlayingAudioId(null)
      setIsPlaying(false)
    }
    if (isRecording) stopRecording()
    deleteRecording()

    setPracticeMode(true)
    setPieceIndex(0)
    setPracticeStep(STEP_LISTEN)
  }

  function stopRecordingPlayback() {
    if (recordedAudioRef.current) {
      recordedAudioRef.current.pause()
    }
    setIsPlayingRecording(false)
  }

  function exitPractice() {
    setPracticeMode(false)
    setPracticeStep(STEP_LISTEN)
    setPieceIndex(0)
    setIsComparing(false)
    practiceStopRef.current = null
    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setPlayingAudioId(null)
      setIsPlaying(false)
    }
    if (isRecording) stopRecording()
    if (isPlayingRecording) stopRecordingPlayback()
  }

  function practiceListenPiece() {
    if (!audioRef.current || !duration) return
    const { start, end } = getPieceTimes(pieceIndex)
    audioRef.current.currentTime = start
    practiceStopRef.current = end
    audioRef.current.play()
    setPlayingAudioId(audioId)
    setIsPlaying(true)
    setPracticeStep(STEP_LISTEN)
  }

  async function practiceRecordPiece() {
    deleteRecording()
    setPracticeStep(STEP_RECORD)
    await startRecording()
  }

  function practiceCompare() {
    // Play lesson piece first, then recording
    if (!audioRef.current || !duration) return
    const { start, end } = getPieceTimes(pieceIndex)
    audioRef.current.currentTime = start
    practiceStopRef.current = end
    setIsComparing(true)
    setPracticeStep(STEP_COMPARE)
    audioRef.current.play()
    setPlayingAudioId(audioId)
    setIsPlaying(true)
  }

  function playRecordingForCompare() {
    if (!recordedAudioRef.current) return
    recordedAudioRef.current.currentTime = 0
    recordedAudioRef.current.play()
    setIsPlayingRecording(true)
  }

  function practiceRetry() {
    if (isPlayingRecording) stopRecordingPlayback()
    deleteRecording()
    practiceListenPiece()
  }

  function practiceNext() {
    if (isPlayingRecording) stopRecordingPlayback()
    deleteRecording()
    const nextIdx = pieceIndex + 1
    if (nextIdx < totalPieces) {
      setPieceIndex(nextIdx)
      setPracticeStep(STEP_LISTEN)
      // Auto-play next piece after a short delay
      setTimeout(() => {
        if (!audioRef.current || !duration) return
        const { start, end } = getPieceTimes(nextIdx)
        audioRef.current.currentTime = start
        practiceStopRef.current = end
        audioRef.current.play()
        setPlayingAudioId(audioId)
        setIsPlaying(true)
      }, 300)
    } else {
      // Finished all pieces
      exitPractice()
    }
  }

  function practicePrev() {
    if (isPlayingRecording) stopRecordingPlayback()
    deleteRecording()
    const prevIdx = Math.max(0, pieceIndex - 1)
    setPieceIndex(prevIdx)
    setPracticeStep(STEP_LISTEN)
    setTimeout(() => {
      if (!audioRef.current || !duration) return
      const { start, end } = getPieceTimes(prevIdx)
      audioRef.current.currentTime = start
      practiceStopRef.current = end
      audioRef.current.play()
      setPlayingAudioId(audioId)
      setIsPlaying(true)
    }, 300)
  }

  // --- Dev: Word Marking Mode (Segment-based Yes/No/Undo) ---

  function enterMarking() {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setPlayingAudioId(null)
      setIsPlaying(false)
    }
    wordsRef.current = (geezText || '').split(/\s+/).filter(t => t && isGeezWord(t))
    wordMarksRef.current = []
    setWordMarks([])
    setMarkingMode(true)
    if (onMarkingWords) onMarkingWords([], null)
  }

  function exitMarking() {
    stopReviewMode()
    setMarkingMode(false)
    wordMarksRef.current = []
    setWordMarks([])
    if (onMarkingWords) onMarkingWords(null, null)
    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setPlayingAudioId(null)
      setIsPlaying(false)
    }
  }

  // --- Manual spacebar marking ---
  function startManualMarking() {
    if (!audioRef.current) return
    stopReviewMode()
    audioRef.current.pause()
    audioRef.current.currentTime = 0
    // Auto-stamp word 0 at time 0; spacebar marks word 1 onward
    const initial = [0]
    wordMarksRef.current = initial
    setWordMarks(initial)
    if (onMarkingWords) onMarkingWords(initial, markNextWord)
    audioRef.current.play()
    setPlayingAudioId(audioId)
    setIsPlaying(true)
  }

  function markNextWord() {
    if (!audioRef.current) return
    const current = wordMarksRef.current
    if (current.length >= wordsRef.current.length) return
    const time = parseFloat(audioRef.current.currentTime.toFixed(2))
    const newMarks = [...current, time]
    wordMarksRef.current = newMarks
    setWordMarks(newMarks)
    if (onMarkingWords) onMarkingWords(newMarks, markNextWord)
  }

  // Get segment boundaries from dips
  // Use dip.edge (where energy starts dropping = word just ended)
  // instead of dip.time (deepest point of silence, which bleeds into next word)
  function getSegStart(segIdx, dips) {
    return segIdx === 0 ? 0 : (dips[segIdx - 1].edge || dips[segIdx - 1].time)
  }
  function getSegEnd(segIdx, dips, dur) {
    return segIdx < dips.length ? (dips[segIdx].edge || dips[segIdx].time) : dur
  }

  // Play a specific audio segment and pause at its end
  function playSegment(segIdx, dips, dur) {
    if (!audioRef.current) return
    const a = audioRef.current
    const start = getSegStart(segIdx, dips)
    const end = getSegEnd(segIdx, dips, dur)

    // Clear any previous stop mechanism
    if (segmentStopRef.current) {
      cancelAnimationFrame(segmentStopRef.current)
      segmentStopRef.current = null
    }

    console.log(`[playSegment] seg ${segIdx}: ${start.toFixed(2)}s → ${end.toFixed(2)}s (duration: ${(end-start).toFixed(2)}s)`)

    a.currentTime = start
    a.play().then(() => {
      console.log(`[playSegment] play() resolved, currentTime: ${a.currentTime.toFixed(2)}s`)
      setPlayingAudioId(audioId)
      setIsPlaying(true)

      // Poll with requestAnimationFrame — checks every frame, very accurate
      function checkEnd() {
        if (!a || a.paused) {
          console.log(`[playSegment] stopped externally at ${a?.currentTime?.toFixed(2)}s`)
          return
        }
        if (a.currentTime >= end - 0.02) {
          console.log(`[playSegment] reached end at ${a.currentTime.toFixed(2)}s, pausing`)
          a.pause()
          setIsPlaying(false)
          segmentStopRef.current = null
          return
        }
        segmentStopRef.current = requestAnimationFrame(checkEnd)
      }
      segmentStopRef.current = requestAnimationFrame(checkEnd)
    }).catch(err => console.log('[playSegment] play() rejected:', err))
  }

  // Start auto-review: analyze dips, then begin segment-by-segment review
  async function startAutoReview() {
    if (!audioRef.current) return
    const filename = getFilenameFromUrl(audioUrl)
    if (!filename) {
      setReviewStatus('Cannot determine audio filename')
      return
    }

    setReviewStatus('Analyzing audio...')
    setReviewActive(true)

    try {
      const resp = await fetch('/api/analyze-dips', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioFile: filename })
      })
      if (!resp.ok) {
        const err = await resp.json()
        throw new Error(err.error || 'Analysis failed')
      }

      const result = await resp.json()
      const allDips = result.dips
      const dur = result.duration
      const needed = wordsRef.current.length - 1 // N words need N-1 boundaries

      // Select the strongest N-1 dips by depth, then sort by time
      const dips = allDips.length <= needed
        ? allDips
        : [...allDips].sort((a, b) => b.depth - a.depth)
            .slice(0, needed)
            .sort((a, b) => a.time - b.time)

      console.log(`[Auto-Review] ${allDips.length} gaps found → selected ${dips.length} strongest for ${wordsRef.current.length} words`)
      console.log('[Auto-Review] All gaps:', allDips.map(d => `${d.edge.toFixed(2)}s (${d.durationMs}ms)`).join(', '))
      console.log('[Auto-Review] Selected:', dips.map(d => `${d.edge.toFixed(2)}s (${d.durationMs}ms)`).join(', '))

      // Store in refs for use in callbacks
      reviewDipsRef.current = dips
      reviewDurationRef.current = dur
      reviewSegIndexRef.current = 0
      reviewWordIndexRef.current = 0
      reviewExtendCountRef.current = 0
      reviewTimestampsRef.current = []
      reviewGroupedRef.current = new Set()
      reviewHistoryRef.current = []

      setReviewDips(dips)
      setReviewSegIndex(0)
      setReviewWordIndex(0)
      setReviewExtendCount(0)
      setReviewTimestamps([])
      setReviewGrouped(new Set())

      const totalSegs = dips.length + 1
      setReviewStatus(`${dips.length} dips → ${totalSegs} segments for ${wordsRef.current.length} words. Playing segment 1...`)

      // Play first segment
      playSegment(0, dips, dur)
    } catch (err) {
      setReviewStatus('Error: ' + err.message)
      setReviewActive(false)
    }
  }

  function pushReviewHistory() {
    reviewHistoryRef.current.push({
      segIndex: reviewSegIndexRef.current,
      wordIndex: reviewWordIndexRef.current,
      extendCount: reviewExtendCountRef.current,
      timestamps: [...reviewTimestampsRef.current],
      grouped: new Set(reviewGroupedRef.current)
    })
  }

  function updateReviewRefs(segIdx, wordIdx, extCount, timestamps, grouped) {
    reviewSegIndexRef.current = segIdx
    reviewWordIndexRef.current = wordIdx
    reviewExtendCountRef.current = extCount
    reviewTimestampsRef.current = timestamps
    reviewGroupedRef.current = grouped
    setReviewSegIndex(segIdx)
    setReviewWordIndex(wordIdx)
    setReviewExtendCount(extCount)
    setReviewTimestamps(timestamps)
    setReviewGrouped(grouped)
  }

  function reviewYes() {
    if (!reviewActive) return
    const dips = reviewDipsRef.current
    const dur = reviewDurationRef.current
    const wordIdx = reviewWordIndexRef.current
    const segIdx = reviewSegIndexRef.current
    const extCount = reviewExtendCountRef.current
    const ts = [...reviewTimestampsRef.current]
    const grouped = new Set(reviewGroupedRef.current)

    if (wordIdx >= wordsRef.current.length) return

    pushReviewHistory()

    // Stamp all highlighted words at this segment's start time
    const segStart = getSegStart(segIdx, dips)
    const count = 1 + extCount
    for (let i = 0; i < count; i++) {
      const wi = wordIdx + i
      if (wi < wordsRef.current.length) {
        ts[wi] = parseFloat(segStart.toFixed(2))
        if (i > 0) grouped.add(wi)
      }
    }

    const newWordIdx = wordIdx + count
    const newSegIdx = segIdx + 1

    updateReviewRefs(newSegIdx, newWordIdx, 0, ts, grouped)

    if (newWordIdx >= wordsRef.current.length) {
      setReviewStatus(`All ${wordsRef.current.length} words stamped! Save when ready.`)
      return
    }
    if (newSegIdx > dips.length) {
      setReviewStatus(`No more segments! ${wordsRef.current.length - newWordIdx} words left. Save partial and stamp rest manually.`)
      return
    }

    const totalSegs = dips.length + 1
    setReviewStatus(`Segment ${newSegIdx + 1}/${totalSegs} | Word ${newWordIdx + 1}/${wordsRef.current.length}: "${wordsRef.current[newWordIdx]}"`)
    playSegment(newSegIdx, dips, dur)
  }

  function reviewNo() {
    if (!reviewActive) return
    const wordIdx = reviewWordIndexRef.current
    const extCount = reviewExtendCountRef.current

    if (wordIdx >= wordsRef.current.length) return

    pushReviewHistory()

    let newExtCount = extCount + 1
    // Don't extend past the word list
    if (wordIdx + newExtCount >= wordsRef.current.length) {
      newExtCount = wordsRef.current.length - wordIdx - 1
    }

    reviewExtendCountRef.current = newExtCount
    setReviewExtendCount(newExtCount)

    const highlightedWords = wordsRef.current.slice(wordIdx, wordIdx + 1 + newExtCount).join(' ')
    setReviewStatus(`Extended → "${highlightedWords}" | Press Yes to confirm, No to extend more`)

    // Replay the segment
    playSegment(reviewSegIndexRef.current, reviewDipsRef.current, reviewDurationRef.current)
  }

  function reviewUndoAction() {
    if (!reviewActive || reviewHistoryRef.current.length === 0) return

    const prev = reviewHistoryRef.current.pop()
    updateReviewRefs(prev.segIndex, prev.wordIndex, prev.extendCount, prev.timestamps, prev.grouped)

    const dips = reviewDipsRef.current
    const totalSegs = dips.length + 1
    setReviewStatus(`Undone → Segment ${prev.segIndex + 1}/${totalSegs} | Word ${prev.wordIndex + 1}/${wordsRef.current.length}: "${wordsRef.current[prev.wordIndex]}"`)

    playSegment(prev.segIndex, reviewDipsRef.current, reviewDurationRef.current)
  }

  function stopReviewMode() {
    setReviewActive(false)
    setReviewStatus('')
    setReviewDips([])
    if (segmentStopRef.current) {
      cancelAnimationFrame(segmentStopRef.current)
      segmentStopRef.current = null
    }
  }

  async function exportMarks() {
    // Build final timestamps from review or manual marks
    const finalMarks = wordMarksRef.current.length > 0
      ? [...wordMarksRef.current]
      : [...reviewTimestampsRef.current]

    const filename = getFilenameFromUrl(audioUrl)
    console.log('Timestamps:', finalMarks, '| Words:', wordsRef.current.length)
    const chantEntry = {
      id: sectionId,
      audio: filename || sectionId,
      timestamps: finalMarks
    }

    if (import.meta.env.DEV) {
      try {
        const res = await fetch('/api/save-timestamps', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categoryId, chant: chantEntry })
        })
        const result = await res.json()
        if (result.ok) {
          alert(`Saved! ${finalMarks.length} word marks for "${sectionId}"\nCategory: ${result.category} (${result.total} chants total)`)
        } else {
          alert(`Save failed: ${result.error}`)
        }
      } catch (err) {
        alert(`Save failed: ${err.message}\nFalling back to clipboard.`)
        navigator.clipboard.writeText(JSON.stringify(chantEntry, null, 2))
      }
    } else {
      // Production: save to Firestore so timestamps sync back to an admin.
      // Requires sign-in (Firestore rules restrict writes to the marking team).
      if (!user) {
        alert('Please sign in with Google first, then Save again.\n(Your marks were kept — just sign in and press Save.)')
        return
      }
      try {
        const docId = `${categoryId}_${sectionId}`
        await setDoc(doc(db, 'wordTimestamps', docId), {
          categoryId,
          chant: chantEntry,
          markedBy: user.email || null,
          updatedAt: serverTimestamp()
        })
        alert(`Saved to cloud!\n${finalMarks.length} word marks for "${sectionId}" (category ${categoryId})`)
      } catch (err) {
        console.error('Firestore save failed:', err)
        const json = JSON.stringify(chantEntry, null, 2)
        navigator.clipboard.writeText(json).then(() => {
          alert(`Cloud save failed (${err.message}).\nCopied to clipboard as a fallback — please send this to the admin.`)
        })
      }
    }
    console.log('Word timestamps (category ' + categoryId + '):', chantEntry)
    stopReviewMode()
  }

  // Keyboard handler for review mode
  useEffect(() => {
    if (!markingMode) return
    function handleMarkKey(e) {
      if (reviewActive) {
        // Auto-review keyboard shortcuts
        if (e.key === 'Enter') {
          e.preventDefault()
          e.stopImmediatePropagation()
          reviewYes()
        } else if (e.key === 'n' || e.key === 'N') {
          e.preventDefault()
          e.stopImmediatePropagation()
          reviewNo()
        } else if (e.key === 'Backspace') {
          e.preventDefault()
          e.stopImmediatePropagation()
          reviewUndoAction()
        } else if (e.key === ' ') {
          e.preventDefault()
          e.stopImmediatePropagation()
          // Replay current segment
          playSegment(reviewSegIndexRef.current, reviewDipsRef.current, reviewDurationRef.current)
        } else if (e.key === 'Escape') {
          exitMarking()
        }
      } else {
        // Manual marking mode shortcuts
        if (e.key === ' ') {
          if (wordMarksRef.current.length < wordsRef.current.length) {
            e.preventDefault()
            e.stopImmediatePropagation()
            markNextWord()
          }
        } else if (e.key === 'Escape') {
          exitMarking()
        }
      }
    }
    window.addEventListener('keydown', handleMarkKey, true)
    return () => window.removeEventListener('keydown', handleMarkKey, true)
  }, [markingMode, reviewActive])

  // --- SVG Icons ---
  const IconPlay = <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="6,3 20,12 6,21"/></svg>
  const IconPause = <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="3" width="4" height="18"/><rect x="15" y="3" width="4" height="18"/></svg>
  const IconStop = <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
  const IconSkipBack = <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="19,20 9,12 19,4"/><rect x="5" y="4" width="2" height="16"/></svg>
  const IconSkipFwd = <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,4 15,12 5,20"/><rect x="17" y="4" width="2" height="16"/></svg>
  const IconMic = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
  const IconTarget = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
  const IconVolume = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
  const IconShuffle = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>
  const IconRefresh = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
  const IconCheck = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  const IconShare = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
  const IconSave = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
  const IconX = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>

  // --- Render ---

  // Practice mode UI
  if (practiceMode) {
    const { start, end } = getPieceTimes(pieceIndex)
    const pieceStartPct = duration ? (start / duration) * 100 : 0
    const pieceEndPct = duration ? (end / duration) * 100 : 100

    return (
      <div className="audio-player-wrapper">
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          preload="metadata"
        />

        <div className="practice-panel">
          <div className="practice-header">
            <span className="practice-title">Practice Mode</span>
            <span className="practice-piece">Piece {pieceIndex + 1} of {totalPieces}</span>
            <button className="practice-exit" onClick={exitPractice} title="Exit practice">{IconX}</button>
          </div>

          {/* Progress bar showing full track with current piece highlighted */}
          <div className="practice-progress-wrapper">
            <div className="progress-bar practice-progress" onClick={handleSeek}>
              <div className="piece-highlight" style={{
                left: `${pieceStartPct}%`,
                width: `${pieceEndPct - pieceStartPct}%`
              }} />
              <div className="progress-fill" style={{ width: `${progress}%` }} />
              {breakpoints.map((bp, i) => (
                <div key={i} className="breakpoint-marker" style={{ left: `${bp}%` }} />
              ))}
            </div>
            <span className="time-display">
              {formatTime(audioRef.current?.currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Step-specific controls */}
          <div className="practice-controls">
            {practiceStep === STEP_LISTEN && (
              <>
                {isPlaying ? (
                  <button className="practice-btn listen-btn" onClick={() => {
                    audioRef.current?.pause()
                    setPlayingAudioId(null)
                    setIsPlaying(false)
                  }}>
                    {IconPause} Listening...
                  </button>
                ) : (
                  <button className="practice-btn listen-btn" onClick={practiceListenPiece}>
                    {IconVolume} Listen
                  </button>
                )}
              </>
            )}

            {practiceStep === STEP_RECORD && (
              <>
                {isRecording ? (
                  <button className="practice-btn record-active-btn" onClick={() => {
                    stopRecording()
                  }}>
                    {IconStop} Stop ({formatTime(recordingTime)})
                  </button>
                ) : (
                  <button className="practice-btn record-practice-btn" onClick={practiceRecordPiece}>
                    {IconMic} Record Your Voice
                  </button>
                )}
                {recordingError && (
                  <span className="recording-error">{recordingError}</span>
                )}
                <button className="practice-btn skip-btn" onClick={practiceListenPiece}>
                  {IconVolume} Listen Again
                </button>
              </>
            )}

            {practiceStep === STEP_COMPARE && (
              <>
                {/* Hidden audio for recording playback */}
                {recordedBlobUrl && (
                  <audio
                    ref={recordedAudioRef}
                    src={recordedBlobUrl}
                    onTimeUpdate={() => {
                      if (!recordedAudioRef.current) return
                      const cur = recordedAudioRef.current.currentTime
                      const tot = recordedAudioRef.current.duration
                      setRecProgress(tot ? (cur / tot) * 100 : 0)
                    }}
                    onLoadedMetadata={() => {
                      if (recordedAudioRef.current) setRecDuration(recordedAudioRef.current.duration)
                    }}
                    onEnded={() => {
                      setIsPlayingRecording(false)
                      setRecProgress(0)
                    }}
                  />
                )}

                <button className="practice-btn compare-btn" onClick={practiceCompare}
                  disabled={isPlaying || isPlayingRecording}>
                  {IconShuffle} Compare
                </button>
                <button className="practice-btn retry-btn" onClick={practiceRetry}>
                  {IconRefresh} Retry
                </button>
                <button className="practice-btn next-btn" onClick={practiceNext}>
                  {IconCheck} {pieceIndex + 1 < totalPieces ? 'Next' : 'Finish'}
                </button>
              </>
            )}
          </div>

          {/* Piece navigation */}
          <div className="practice-nav">
            <button className="nav-btn" onClick={practicePrev} disabled={pieceIndex === 0}
              title="Previous piece">{IconSkipBack}</button>
            <span className="practice-nav-label">
              {formatTime(start)} – {formatTime(end)}
            </span>
            <button className="nav-btn" onClick={practiceNext} disabled={pieceIndex >= totalPieces - 1}
              title="Next piece">{IconSkipFwd}</button>
          </div>
        </div>
      </div>
    )
  }

  // Normal mode UI
  return (
    <div className="audio-player-wrapper" onClick={e => e.stopPropagation()}>
      {/* Lesson audio player - Row 1: playback controls */}
      <div className="audio-player">
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          preload="metadata"
        />

        <button className="nav-btn" onClick={jumpToPrevBreakpoint} title="Previous section (←)">
          {IconSkipBack}
        </button>

        <button className="play-btn" onClick={togglePlay}>
          {isPlaying ? IconPause : IconPlay}
        </button>

        <button className="nav-btn" onClick={jumpToNextBreakpoint} title="Next section (→)">
          {IconSkipFwd}
        </button>

        <div className="progress-bar" onClick={handleSeek}>
          <div className="progress-fill" style={{ width: `${progress}%` }} />
          {breakpoints.map((bp, i) => (
            <div key={i} className="breakpoint-marker" style={{ left: `${bp}%` }} />
          ))}
          {duration > 0 && (markingMode ? wordMarks : (savedWordTimestamps || [])).map((t, i) => (
            t !== undefined && <div key={`wm-${i}`} className="word-mark-marker" style={{
              left: `${(t / duration) * 100}%`,
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: 2,
              background: '#FFD700',
              opacity: 0.8,
              zIndex: 2
            }} />
          ))}
        </div>

        <span className="time-display">
          {formatTime(audioRef.current?.currentTime)} / {formatTime(duration)}
        </span>
      </div>

      {/* Why the Record button did nothing. Without this the only outlet for
          recordingError was the practice-mode panel, so outside practice a
          blocked microphone looked like a dead button. */}
      {recordingError && !isRecording && (
        <p className="recording-error-note" role="alert">{recordingError}</p>
      )}

      {/* Row 2: Record + Practice controls */}
      <div className={`audio-actions ${isRecording ? 'audio-actions-recording' : ''}`}>
        <button
          className={`action-btn ${isRecording ? 'action-btn-recording' : 'action-btn-record'}`}
          onClick={e => { e.stopPropagation(); e.preventDefault(); toggleRecord() }}
          title={isRecording ? 'Stop recording' : 'Record your voice'}
        >
          {isRecording ? IconStop : IconMic}
          <span>{isRecording ? `Stop ${formatTime(recordingTime)}` : 'Record'}</span>
        </button>

        {isRecording && (
          <div className="recording-indicator">
            <span className="recording-dot" />
            <span className="recording-indicator-text">Recording...</span>
            {/* Live mic level bar */}
            <div style={{ width: 60, height: 8, background: '#333', borderRadius: 4, marginLeft: 8, overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(micLevel * 100, 100)}%`,
                height: '100%',
                background: micStatus === 'none' ? '#f44336' : micLevel > 0.05 ? '#4CAF50' : '#D4A027',
                transition: 'width 0.05s',
              }} />
            </div>
            {/* A quiet room is not a broken microphone — only say NO MIC when
                the track is actually missing or muted. */}
            <span style={{
              fontSize: '0.65rem',
              marginLeft: 4,
              color: micStatus === 'none' ? '#f44336' : micLevel > 0.05 ? '#4CAF50' : '#D4A027',
            }}>
              {micStatus === 'none' ? 'NO MIC' : micLevel > 0.05 ? 'MIC OK' : micStatus === 'live' ? 'QUIET' : 'WAITING'}
            </span>
          </div>
        )}

        {breakpoints.length > 0 && !isRecording && (
          <button className="action-btn action-btn-practice" onClick={enterPractice} title="Practice mode">
            {IconTarget}
            <span>Practice</span>
          </button>
        )}
      </div>

      {/* Dev: Word Marking Mode */}
      {devMode && !markingMode && (
        <button className="practice-enter-btn" onClick={enterMarking} title="Mark word timestamps (dev)"
          style={{ marginTop: 4, fontSize: '0.75rem', color: '#999' }}>
          Mark Words
        </button>
      )}

      {markingMode && (
        <div className="marking-panel" style={{
          background: '#1a1a2e', color: '#fff', borderRadius: 8, padding: 12, marginTop: 8, overflow: 'hidden', boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Word Marking Mode</span>
            <button onClick={exitMarking} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>{IconX}</button>
          </div>

          <div style={{ fontSize: '0.8rem', marginBottom: 8, color: '#aaa' }}>
            Words: {wordsRef.current.length} | Marked: {wordMarks.length}
            {wordMarks.length > 0 && ` | Last: ${wordMarks[wordMarks.length - 1]}s`}
          </div>

          {/* Show words with highlighting */}
          <div style={{ fontSize: '0.9rem', lineHeight: 2, marginBottom: 10, overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%' }}>
            {wordsRef.current.map((word, i) => {
              const manualProgress = Math.max(0, wordMarks.length - 1)
              const isMarked = i < manualProgress
              const isNext = i === manualProgress
              return (
                <span
                  key={i}
                  onClick={(e) => { e.stopPropagation(); if (isNext && !reviewActive) markNextWord() }}
                  style={{
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: isMarked ? 'rgba(45,142,126,0.3)' : isNext ? 'rgba(255,200,50,0.25)' : 'transparent',
                    color: isMarked ? '#3AA08E' : isNext ? '#FFD700' : '#666',
                    marginRight: 4,
                    cursor: isNext ? 'pointer' : 'default',
                    border: isNext ? '1px solid rgba(255,200,50,0.5)' : '1px solid transparent',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {word}
                </span>
              )
            })}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={startManualMarking} style={{
              padding: '6px 16px', borderRadius: 6, border: 'none',
              background: '#2D8E7E', color: '#fff', cursor: 'pointer', fontSize: '0.85rem'
            }}>
              {wordMarks.length > 0 || isPlaying ? 'Restart' : 'Start'}
            </button>

            {isPlaying && !reviewActive && (
              <span style={{ padding: '6px 12px', color: '#3AA08E', fontSize: '0.85rem' }}>
                Press SPACEBAR to mark each word...
              </span>
            )}

            {wordMarks.length > 0 && !isPlaying && (
              <button onClick={exportMarks} style={{
                padding: '6px 16px', borderRadius: 6, border: '1px solid #3AA08E',
                background: 'transparent', color: '#3AA08E', cursor: 'pointer', fontSize: '0.85rem'
              }}>
                Save Timestamps
              </button>
            )}
          </div>
        </div>
      )}

      {/* Recorded audio playback (normal mode) */}
      {recordedBlobUrl && !isRecording && (
        <div className="rec-playback">
          <audio
              ref={recordedAudioRef}
              src={recordedBlobUrl}
              onTimeUpdate={() => {
                if (!recordedAudioRef.current) return
                const cur = recordedAudioRef.current.currentTime
                const tot = recordedAudioRef.current.duration
                setRecProgress(tot ? (cur / tot) * 100 : 0)
              }}
              onLoadedMetadata={() => {
                if (recordedAudioRef.current) setRecDuration(recordedAudioRef.current.duration)
              }}
              onEnded={() => {
                setIsPlayingRecording(false)
                setRecProgress(0)
              }}
            />

          <div className="rec-playback-header">
            <span className="rec-playback-label">Your Recording</span>
            <div className="rec-playback-actions">
              <button className="rec-action-btn" onClick={(e) => { e.stopPropagation(); shareRecording(); }} title="Share recording">
                {IconShare}
              </button>
              <button className="rec-action-btn" onClick={(e) => { e.stopPropagation(); saveRecording(); }} title="Save recording">
                {IconSave}
              </button>
              <button className="rec-action-btn rec-action-delete" onClick={deleteRecording} title="Delete recording">
                {IconX}
              </button>
            </div>
          </div>

          {shareNote && (
            <div className="rec-share-note">{shareNote}</div>
          )}

          {/* Desktop: the file is in Downloads and the teacher attaches it.
              Deliberately does not say "sent" — nothing has been sent. */}
          {savedFileName && (
            <div className="rec-saved-note">
              <p>
                <strong>Recording saved to your Downloads.</strong><br />
                Open Telegram or WhatsApp and attach <code>{savedFileName}</code>
              </p>
              {/* The installed desktop apps register these schemes; the web
                  URLs would show a QR login page instead of handing off to
                  them. Nothing happens visibly if an app is not installed, so
                  the web versions stay available underneath. */}
              <div className="rec-saved-actions">
                <a href="tg://">Open Telegram</a>
                <a href="whatsapp://send">Open WhatsApp</a>
              </div>
              <p className="rec-saved-web">
                No app installed? Use <a href="https://web.telegram.org/a/" target="_blank" rel="noopener noreferrer">Telegram Web</a>
                {' '}or <a href="https://web.whatsapp.com/" target="_blank" rel="noopener noreferrer">WhatsApp Web</a>.
              </p>
            </div>
          )}

          <div className="rec-playback-controls">
            <button className="play-btn rec-play-btn" onClick={togglePlayRecording}>
              {isPlayingRecording ? IconPause : IconPlay}
            </button>

            <div className="progress-bar" onClick={handleRecSeek}>
              <div className="progress-fill rec-fill" style={{ width: `${recProgress}%` }} />
            </div>

            <span className="time-display">
              {formatTime(recordedAudioRef.current?.currentTime)} / {formatTime(recDuration)}
            </span>
          </div>

          {/* Recording diagnostics — dev mode only; users saw raw strings
              like "Decoded: 15.3s, 48000Hz, maxAmp=0.0421" in production. */}
          {devMode && debugInfo && (
            <div style={{ fontSize: '0.7rem', color: '#f0a', padding: '4px 8px', wordBreak: 'break-all' }}>
              {debugInfo}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
